import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography, 
    CircularProgress, 
    TextField, 
    Button,   
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const DelayCodePage = () => {
    useAuthCheck(); 
    const navigate = useNavigate();
    const { delayCodeId } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [maxDelayIndex, setMaxDelayIndex] = useState(100);    

    const [formValues, setFormValues] = useState({
        delayIndex: maxDelayIndex + 1,
        delayCode: '',
        description: '',
        category: '',
    });

    
    const fetchMaxDelayIndex = useCallback(async () => {       
        const res = await api.get('/delaycodes/maxdelayindex');
        setMaxDelayIndex(res.data.maxDelayIndex);          
     }, []);
   
    const fetchDelayCode = useCallback(async () => {       
        if (delayCodeId) {
            const res = await api.get(`/delaycodes/${delayCodeId}`);

            setFormValues({
                delayIndex: res.data.delayIndex || 0,
                delayCode: res.data.delayCode || '',
                description: res.data.description || '',
                category: res.data.category || '',
            });
        }        
    }, [delayCodeId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                await Promise.all([
                    fetchMaxDelayIndex(),
                    fetchDelayCode(),
                ]);
                
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch data!");
                setError(error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();
    }, [
        fetchMaxDelayIndex, 
        fetchDelayCode, 
    ]);   

    const checkoutSchema = yup.object().shape({
        delayIndex: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = delayCodeId ? "PUT" : "POST";
            const url = delayCodeId
                ? `/delaycodes/update/${delayCodeId}` 
                : '/delaycodes/new'

                
            const res = await api({
                method,
                url,
                data: {
                    delayIndex: values.delayIndex,
                    delayCode: values.delayCode,
                    description: values.description,
                    category: values.category,
                },
            });

            if (![200, 201].includes(res.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(delayCodeId 
                ? 'Delay code updated successfully' 
                : 'Delay code created successfully'
            );
            
            // onSubmitProps.resetForm();
            navigate("/delaycodes");
        } catch (error) {
            setError(error);
        }
    };

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Delay Code"
                    subtitle={`${loading ? 'Loading...' : delayCodeId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/delaycodes")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Delay index"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.delayIndex}
                                        name="delayIndex"
                                        error={!!touched.delayIndex && !!errors.delayIndex}
                                        helperText={touched.delayIndex && errors.delayIndex}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Delay code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.delayCode}
                                        name="delayCode"
                                        error={!!touched.delayCode && !!errors.delayCode}
                                        helperText={touched.delayCode && errors.delayCode}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Category"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.category}
                                        name="category"
                                        error={!!touched.category && !!errors.category}
                                        helperText={touched.category && errors.category}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {delayCodeId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default DelayCodePage;
